import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getProjectById, updateProjectStatus, deleteProject, deleteProjectHard, syncProjectWatchersToTasks } from '../../api/pmsApi';
import FileAttachment from '../../components/FileAttachment/FileAttachment.js';

function ProjectView({ isInline, onEditInline, calculatedProgress, onDataRefresh, tasksCount }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // 현재 접속자가 가진 권한
  const [isLeaderOrAuthor, setIsLeaderOrAuthor] = useState(false);
  const [isReviewer, setIsReviewer] = useState(false);

  // 검토 의견 모달
  const [showOpinionModal, setShowOpinionModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'approve' | 'reject'
  const [opinionText, setOpinionText] = useState('');
  const [showReadOpinionModal, setShowReadOpinionModal] = useState(false);

  useEffect(() => {
    if (!id || !token) return;

    setIsLoading(true);
    getProjectById(id, token)
      .then(res => {
        if (res.ok) {
          const data = res.data;
          setProject(data);

          // 권한 체크
          const currentUserId = user?.id; // TdxUser id (or userid, depends on backend schema. Usually id in DB)
          setIsLeaderOrAuthor(user?.role === 'admin' || Number(user?.id) === Number(data.author_id) || Number(user?.id) === Number(data.leader_id) || (data.instruction && Number(user?.id) === Number(data.instruction.author_id)));
          setIsReviewer(user?.role === 'admin' || Number(currentUserId) === Number(data.reviewer_id));
        }
      })
      .catch(err => {
        console.error('프로젝트 열람 실패:', err);
        alert('데이터를 불러오는데 실패했습니다.');
        navigate('/pms/projects');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, token, user, navigate]);

  const handleStatusChange = async (newStatus, actionName, opinion = undefined) => {
    if (opinion === undefined) {
      if (!window.confirm(`정말로 ${actionName} 하시겠습니까?`)) return;
    }

    try {
      await updateProjectStatus(id, newStatus, opinion, token);
      alert(`성공적으로 ${actionName} 되었습니다.`);
      if (newStatus === 'review') navigate('/pms/projects/review');
      else if (newStatus === 'rejected') navigate('/pms/projects/rejected');
      else if (newStatus === 'completed') navigate('/pms/projects/completed');
      else if (newStatus === 'canceled') navigate('/pms/projects/canceled');
      else if (newStatus === 'ready') navigate('/pms/projects/ready');
      else navigate('/pms/projects');
    } catch (err) {
      console.error('상태 변경 실패:', err);
      alert('상태 변경에 실패했습니다.');
    }
  };

  const handleWorkspaceStatusToggle = async (newStatus, actionName) => {
    if (!window.confirm(`프로젝트를 ${actionName} 하시겠습니까?`)) return;
    try {
      await updateProjectStatus(id, newStatus, '', token);
      alert(`성공적으로 ${actionName} 되었습니다.`);
      navigate('/pms/project-rooms');
    } catch (err) {
      console.error('상태 변경 실패:', err);
      alert('상태 변경에 실패했습니다.');
    }
  };

  const handleSyncWatchers = async () => {
    if (tasksCount !== undefined && tasksCount === 0) {
      alert('등록된 세부항목이 없습니다.');
      return;
    }
    const watcherCount = project.members ? project.members.filter(m => !m.is_manager).length : 0;
    const msg = watcherCount > 0
      ? '프로젝트 참조자를 모든 세부항목에 일괄 추가하시겠습니까?\n(수동으로 추가된 참조자는 안전하게 유지됩니다.)'
      : '프로젝트 참조자가 없습니다.\n세부항목에서 일괄 등록된 참조자를 모두 삭제하시겠습니까?\n(수동으로 추가된 참조자는 안전하게 유지됩니다.)';
    if (!window.confirm(msg)) return;
    try {
      const res = await syncProjectWatchersToTasks(id, token);
      if (res.ok) {
        alert(watcherCount > 0
          ? '모든 세부항목에 참조자가 일괄 적용되었습니다.'
          : '세부항목에서 일괄 등록된 참조자가 삭제되었습니다.');
        if (onDataRefresh) {
          onDataRefresh();
        }
      } else {
        alert(res.message || '일괄 적용에 실패했습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('일괄 적용 중 오류가 발생했습니다.');
    }
  };

  const submitOpinionAction = (actionType) => {
    if (actionType === 'approve') {
      handleStatusChange('ready', '프로젝트 승인', opinionText);
    } else if (actionType === 'reject') {
      if (!opinionText.trim()) {
        alert('반려 사유(의견)를 입력해주세요.');
        return;
      }
      handleStatusChange('rejected', '프로젝트 반려', opinionText);
    }
    setShowOpinionModal(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('프로젝트를 임시 삭제 처리하시겠습니까?')) return;
    try {
      await deleteProject(id, token);
      alert('프로젝트가 취소/삭제 목록으로 이동되었습니다.');
      navigate('/pms/projects');
    } catch (err) {
      alert('삭제에 실패했습니다.');
    }
  };

  const handleHardDelete = async () => {
    if (!window.confirm('프로젝트와 관련된 모든 데이터 및 첨부파일이 완전히 삭제됩니다.\n\n계속하시겠습니까?')) return;
    if (!window.confirm('🚨 경고 🚨\n데이터가 삭제된 후에는 재등록이 불가능하며 영구적으로 복구할 수 없습니다.\n\n정말 완전 삭제하시겠습니까?')) return;

    try {
      await deleteProjectHard(id, token);
      alert('프로젝트가 완전히 삭제되었습니다.');
      navigate('/pms/projects');
    } catch (err) {
      console.error('완전 삭제 실패:', err);
      alert('완전 삭제에 실패했습니다.');
    }
  };

  const formatCurrency = (val) => {
    if (!val) return '0원';
    return Number(val).toLocaleString('ko-KR') + '원';
  };

  if (isLoading) {
    return <div className="p-5 text-center"><div className="spinner-border text-primary"></div></div>;
  }

  if (!project) {
    return <div className="p-5 text-center text-muted">프로젝트를 찾을 수 없습니다.</div>;
  }

  return (
    <div className="container-fluid p-4">
      {/* 🚀 상단 헤더 (타이틀) */}
      {!isInline && (
        <div className="d-flex justify-content-between align-items-center mb-4 p-3 rounded shadow-sm border-0"
          style={{ background: 'linear-gradient(90deg, #607d8b 0%, #cfd8dc 100%)', color: '#fff' }}>
          <h4 className="mb-0 fw-bold">
            <i className="fas fa-desktop me-2"></i>프로젝트 상세 정보
          </h4>
          <div className="d-flex gap-2">
            {project?.review_opinion && user && (
              user.role === 'admin' ||
              user.id === project.author_id ||
              user.id === project.reviewer_id ||
              user.id === project.leader_id ||
              project.members?.some(m => m.user_id === user.id)
            ) && (
                <button className="btn btn-dark text-white fw-bold btn-sm" onClick={() => setShowReadOpinionModal(true)}>
                  <i className="fas fa-comment-dots me-1"></i>검토 의견
                </button>
              )}
            {(project?.status === 'in_progress' || project?.status === 'ready') && (
              <button className="btn btn-primary fw-bold btn-sm" onClick={() => navigate(`/pms/project-rooms/${id}`)}>
                🚀 프로젝트 Room 입장
              </button>
            )}
            <button className="btn btn-light fw-bold text-dark btn-sm" onClick={() => navigate(-1)}>
              <i className="fas fa-arrow-left me-1"></i>뒤로가기
            </button>
          </div>
        </div>
      )}

      <div className="row g-4">
        {/* 📝 좌측: 본문 내용 및 파일 */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm rounded-4 mb-4">
            <div className="card-header bg-white border-bottom-0 pt-4 pb-0">
              <h5 className="fw-bold text-dark d-flex align-items-center">
                <i className="fas fa-desktop me-2 text-primary"></i>기획안 (프로젝트 상세)
                <span className={`badge ${(() => {
                  const currentProgress = project.progress_rate || 0;
                  const todayStr = new Date().toISOString().split('T')[0];
                  if (currentProgress === 100) return 'bg-success';
                  if (project.end_date && project.end_date < todayStr) return 'bg-danger';
                  return 'bg-primary';
                })()} ms-3 rounded-pill`} style={{ fontSize: '0.75rem' }}>진척도 {project.progress_rate}%</span>
              </h5>
            </div>

            <div className="card-body">
              {/* 프로젝트 제목 */}
              <div className="mb-3">
                <label className="form-label fw-bold"><span className="text-primary fw-bold me-2 fs-5">H</span>프로젝트 명</label>
                <input
                  type="text"
                  className="form-control"
                  style={{ backgroundColor: '#f8f9fa', color: '#212529' }}
                  value={project.title || ''}
                  readOnly
                />
              </div>

              {/* HTML 본문 렌더링 영역 */}
              <div className="mb-4 mt-1">
                <label className="form-label fw-bold mb-2"><i className="fas fa-align-left text-primary fs-5 me-2"></i>상세 내용 (기획안)</label>
                <div
                  className="p-4 bg-white border"
                  style={{ minHeight: '350px', fontSize: '1rem', lineHeight: '1.6', borderTop: '2px solid #6c757d' }}
                  dangerouslySetInnerHTML={{ __html: project.content || '<p class="text-muted text-center mt-5">본문 내용이 없습니다.</p>' }}
                />
              </div>

              {/* 첨부파일 리스트 (mode="view") */}
              {project.files && project.files.length > 0 && (
                <div>
                  <div className="p-0 bg-white border">
                    <div className="bg-light p-2 border-bottom fw-bold text-secondary">
                      <i className="fas fa-paperclip me-2"></i>첨부파일
                    </div>
                    <div className="p-3">
                      <FileAttachment files={project.files} mode="view" />
                    </div>
                  </div>
                </div>
              )}

              {/* 🚀 좌측 하단 액션 버튼 (상태 변경용) */}
              <div className="d-flex justify-content-end flex-wrap gap-2 mt-4 pt-3 border-top">
                {/* 취소되거나 삭제된 상태일 때: 복구(재등록) 및 완전삭제 버튼 표시 */}
                {(project.status === 'canceled' || project.is_deleted) && isLeaderOrAuthor && (
                  <>
                    <button className="btn btn-info fw-bold text-white px-3 py-2 rounded-3 shadow-sm text-nowrap" onClick={() => handleStatusChange(project.status === 'canceled' ? 'ready' : project.status, '재등록')}>
                      <i className="fas fa-undo me-1"></i> 재등록
                    </button>
                    {/* 완전 삭제 버튼: 삭제된 상태(is_deleted)일 때만 표시 */}
                    {project.is_deleted ? (
                      <button className="btn btn-danger fw-bold px-3 py-2 rounded-3 shadow-sm text-nowrap" onClick={handleHardDelete}>
                        <i className="fas fa-trash-alt me-1"></i> 완전 삭제
                      </button>
                    ) : null}
                  </>
                )}

                {/* 정상 상태일 때 (취소, 삭제 아님) */}
                {project.status !== 'canceled' && !project.is_deleted && (
                  <>
                    {/* 시작 전(ready)인 프로젝트를 진행 중(in_progress)으로 변경하는 버튼 */}
                    {project.status === 'ready' && isLeaderOrAuthor && (calculatedProgress !== undefined ? calculatedProgress : (project.progress_rate || 0)) < 100 && (
                      <button className="btn btn-primary fw-bold px-3 py-2 rounded-3 shadow-sm text-nowrap" onClick={() => isInline ? handleWorkspaceStatusToggle('in_progress', '진행처리') : handleStatusChange('in_progress', '진행처리')}>
                        <i className="fas fa-play me-1"></i> 진행 처리
                      </button>
                    )}

                    {/* 진행 중(in_progress)인 프로젝트를 시작 전(ready)으로 되돌리는 버튼 */}
                    {project.status === 'in_progress' && isLeaderOrAuthor && (calculatedProgress !== undefined ? calculatedProgress : (project.progress_rate || 0)) < 100 && (
                      <button className="btn btn-warning text-dark fw-bold px-3 py-2 rounded-3 shadow-sm text-nowrap" onClick={() => isInline ? handleWorkspaceStatusToggle('ready', '시작전 처리') : handleStatusChange('ready', '시작전 처리')}>
                        <i className="fas fa-pause me-1"></i> 시작전 처리
                      </button>
                    )}

                    {/* 진행중(in_progress)인 프로젝트를 완료 처리하는 버튼 */}
                    {project.status === 'in_progress' && isLeaderOrAuthor && (
                      <button
                        className={`btn btn-${(calculatedProgress !== undefined ? calculatedProgress : (project.progress_rate || 0)) >= 100 ? 'success' : 'secondary'} fw-bold px-3 py-2 rounded-3 shadow-sm text-nowrap`}
                        onClick={() => {
                          const currentProgress = calculatedProgress !== undefined ? calculatedProgress : (project.progress_rate || 0);
                          if (currentProgress < 100) return;
                          const isReviewer = String(project.reviewer_id) === String(user?.id);
                          const hasReviewer = !!project.reviewer_id;
                          const isAlreadyCompletedOnce = !!project.completed_date;
                          
                          if (hasReviewer && !isReviewer && !isAlreadyCompletedOnce) {
                            handleStatusChange('review', '검토요청(결재상신)');
                          } else {
                            handleStatusChange('completed', '완료처리');
                          }
                        }}
                        disabled={(calculatedProgress !== undefined ? calculatedProgress : (project.progress_rate || 0)) < 100}
                        title={(calculatedProgress !== undefined ? calculatedProgress : (project.progress_rate || 0)) < 100 ? "진척도가 100%가 되어야 완료할 수 있습니다." : ""}
                      >
                        <i className={project.reviewer_id && String(project.reviewer_id) !== String(user?.id) && !project.completed_date ? "fas fa-paper-plane me-1" : "fas fa-flag-checkered me-1"}></i> 
                        {project.reviewer_id && String(project.reviewer_id) !== String(user?.id) && !project.completed_date ? '검토 요청' : '완료 처리'}
                      </button>
                    )}

                    {/* 완료(completed)된 프로젝트를 다시 진행 중(in_progress)으로 되돌리는 버튼 */}
                    {project.status === 'completed' && isLeaderOrAuthor && (
                      <button className="btn btn-primary fw-bold px-3 py-2 rounded-3 shadow-sm text-nowrap" onClick={() => isInline ? handleWorkspaceStatusToggle('in_progress', '진행 재개') : handleStatusChange('in_progress', '진행 재개')}>
                        <i className="fas fa-play me-1"></i> 진행 재개
                      </button>
                    )}

                    {/* 프로젝트 승인/반려 (리뷰어 전용) */}
                    {project.status === 'review' && isReviewer && (
                      <>
                        <button className="btn btn-success fw-bold px-3 py-2 rounded-3 shadow-sm text-nowrap" onClick={() => { setModalType('approve'); setShowOpinionModal(true); }}>
                          <i className="fas fa-check-circle me-1"></i> 승인
                        </button>
                        <button className="btn btn-danger fw-bold px-3 py-2 rounded-3 shadow-sm text-nowrap" onClick={() => { setModalType('reject'); setShowOpinionModal(true); }}>
                          <i className="fas fa-ban me-1"></i> 반려
                        </button>
                      </>
                    )}

                    {/* 프로젝트 취소 버튼 (ready 이나 in_progress 일 때) */}
                    {(project.status === 'ready' || project.status === 'in_progress') && isLeaderOrAuthor && (
                      <button className="btn btn-outline-secondary fw-bold px-3 py-2 rounded-3 shadow-sm text-nowrap" onClick={() => handleStatusChange('canceled', '취소처리')}>
                        <i className="fas fa-times-circle me-1"></i> 취소
                      </button>
                    )}

                    {/* 프로젝트 반려(rejected) 상태일 때 재요청하는 버튼 */}
                    {project.status === 'rejected' && isLeaderOrAuthor && (
                      <button className="btn btn-info text-white fw-bold px-3 py-2 rounded-3 shadow-sm text-nowrap" onClick={() => handleStatusChange(project.reviewer_id ? 'review' : 'ready', '재요청')}>
                        <i className="fas fa-paper-plane me-1"></i> 재요청
                      </button>
                    )}

                    {/* 삭제 권한 */}
                    {isLeaderOrAuthor && !isInline && (
                      <button className="btn btn-outline-danger fw-bold px-3 py-2 rounded-3 shadow-sm text-nowrap" onClick={handleDelete}>
                        <i className="fas fa-trash me-1"></i> 삭제
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ⚙️ 우측: 메타데이터 정보 카드 */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm rounded-4 mb-4">
            <div className="card-header bg-light border-bottom-0 pt-4 pb-3 d-flex justify-content-between align-items-center">
              <h5 className="fw-bold text-dark mb-0"><i className="fas fa-info-circle me-2 text-info"></i>프로젝트 개요</h5>
              {project.instruction_id && (
                <button className="btn btn-outline-dark btn-sm rounded-pill px-3 fw-bold shadow-sm" onClick={() => navigate(`/pms/instructions/view/${project.instruction_id}`)}>
                  <i className="fas fa-search me-1"></i>요청원문확인
                </button>
              )}
            </div>
            <div className="card-body pt-1">
              <ul className="list-group list-group-flush">

                <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                  <span className="text-muted fw-bold text-nowrap"><i className="fas fa-pencil-alt text-primary fs-5 me-2"></i>작성자</span>
                  <div className="text-end">
                    {project.author ? (
                      <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap">
                        <span className="text-dark d-flex align-items-center gap-1">
                          <i className="fas fa-user text-secondary me-1"></i>
                          {project.author.name} {project.author.org?.position ? `/ ${project.author.org.position}` : ''}{project.author.org?.dept_name ? `(${project.author.org.dept_name})` : ''}
                        </span>
                        <span className="text-muted">
                          | {project.created_at ? (() => {
                            const d = new Date(project.created_at);
                            if (isNaN(d.getTime())) return project.created_at;
                            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                          })() : '-'}
                        </span>
                      </div>
                    ) : <span className="text-muted small">정보 없음</span>}
                  </div>
                </li>

                <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                  <span className="text-muted fw-bold"><i className="fas fa-calendar-alt text-primary fs-5 me-2"></i>진행 기간</span>
                  <span className="fw-bold text-dark">
                    {project.start_date?.substring(0, 10)} <span className="text-primary mx-1">~</span> {project.end_date?.substring(0, 10)}
                  </span>
                </li>

                {/* 현재 진척도 (읽기 전용) */}
                <li className="list-group-item px-0 py-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted fw-bold"><i className="fas fa-chart-line text-primary fs-5 me-2"></i>현재 진척도</span>
                    <span className={`fw-bolder fs-5 ${(() => {
                      const currentProgress = calculatedProgress !== undefined ? calculatedProgress : (project.progress_rate || 0);
                      const todayStr = new Date().toISOString().split('T')[0];
                      if (currentProgress >= 100) return 'text-success';
                      if (project.end_date && project.end_date < todayStr) return 'text-danger';
                      return 'text-primary';
                    })()}`}>{calculatedProgress !== undefined ? calculatedProgress : (project.progress_rate || 0)}%</span>
                  </div>
                  <div className="progress shadow-sm" style={{ height: '10px', borderRadius: '5px', backgroundColor: '#e9ecef' }}>
                    <div
                      className={`progress-bar progress-bar-striped progress-bar-animated ${(() => {
                        const currentProgress = calculatedProgress !== undefined ? calculatedProgress : (project.progress_rate || 0);
                        const todayStr = new Date().toISOString().split('T')[0];
                        if (currentProgress >= 100) return 'bg-success';
                        if (project.end_date && project.end_date < todayStr) return 'bg-danger';
                        return 'bg-primary';
                      })()}`}
                      role="progressbar"
                      style={{ width: `${calculatedProgress !== undefined ? calculatedProgress : (project.progress_rate || 0)}%`, borderRadius: '5px' }}
                      aria-valuenow={calculatedProgress !== undefined ? calculatedProgress : (project.progress_rate || 0)}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </li>

                <li className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                  <span className="text-muted fw-bold"><i className="fas fa-coins text-primary fs-5 me-2"></i>편성 예산</span>
                  <span className="fw-bold text-dark fs-6">{formatCurrency(project.budget)}</span>
                </li>

                <li className="list-group-item px-0 py-3">
                  <span className="text-muted fw-bold d-block mb-3"><i className="fas fa-user-tie text-primary fs-5 me-2"></i>검토 및 책임자</span>

                  <div className="mb-3">
                    <label className="form-label fw-bold text-muted small mb-2"><i className="fas fa-user-check text-secondary me-2"></i>검토자 (프로젝트생성)</label>
                    <div className="p-2 bg-light rounded-3 border border-dashed">
                      {project.reviewer ? (
                        <div className="d-flex align-items-center gap-2 ms-1" title={project.reviewer.name}>
                          <i className="fas fa-user text-secondary flex-shrink-0"></i>
                          <span className="text-dark" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {project.reviewer.name} {project.reviewer.org?.position ? `/ ${project.reviewer.org.position}` : ''}{project.reviewer.org?.dept_name ? `(${project.reviewer.org.dept_name})` : ''}
                          </span>
                        </div>
                      ) : <span className="text-muted small ms-1">미지정</span>}
                    </div>
                  </div>

                  <div className="mb-2">
                    <label className="form-label fw-bold text-muted small mb-2"><i className="fas fa-user-tie text-secondary me-2"></i>책임자(PM)</label>
                    <div className="p-2 bg-light rounded-3 border border-dashed">
                      {project.leader ? (
                        <div className="d-flex align-items-center gap-2 ms-1" title={project.leader.name}>
                          <i className="fas fa-user text-secondary flex-shrink-0"></i>
                          <span className="text-dark" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {project.leader.name} {project.leader.org?.position ? `/ ${project.leader.org.position}` : ''}{project.leader.org?.dept_name ? `(${project.leader.org.dept_name})` : ''}
                          </span>
                        </div>
                      ) : <span className="text-muted small ms-1">미지정</span>}
                    </div>
                  </div>
                </li>

                <li className="list-group-item px-0 py-3 border-bottom-0">
                  <span className="text-muted fw-bold d-block mb-3"><i className="fas fa-users text-primary fs-5 me-2"></i>담당자</span>
                  <div className="p-3 bg-light rounded-3 min-vh-25 border border-dashed" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                    {project.members && project.members.filter(m => m.is_manager).length > 0 ? (
                      <div className="d-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                        {project.members.filter(m => m.is_manager).map(m => {
                          const u = m.user;
                          const mName = u ? u.name : '알수없음';
                          const mPos = u?.org?.position ? `/ ${u.org.position}` : '';
                          const mDept = u?.org?.dept_name ? `(${u.org.dept_name})` : '';
                          const display = `${mName} ${mPos}${mDept}`;
                          return (
                            <span key={m.user_id} className="badge bg-white text-dark border shadow-sm px-3 py-2 d-flex align-items-center"
                              style={{ fontSize: '0.75rem', fontWeight: '500', minWidth: 0 }} title={display}>
                              <i className="fas fa-user text-secondary me-2 flex-shrink-0"></i>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {display}
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-2">
                        지정된 담당자가 없습니다.
                      </div>
                    )}
                  </div>
                </li>

                <li className="list-group-item px-0 py-3 border-bottom-0">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="text-muted fw-bold"><i className="fas fa-share-square text-info fs-5 me-2"></i>참조자</span>
                      {isLeaderOrAuthor && (tasksCount === undefined || tasksCount > 0) && (
                        <button type="button" className="btn btn-sm btn-outline-info rounded-pill px-3" onClick={handleSyncWatchers}>
                          <i className="fas fa-sync-alt me-1"></i>세부항목 일괄적용
                        </button>
                      )}
                    </div>
                    <div className="p-3 bg-light rounded-3 min-vh-25 border border-dashed" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {project.members && project.members.filter(m => !m.is_manager).length > 0 ? (
                        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                          {project.members.filter(m => !m.is_manager).map(m => {
                            const u = m.user;
                            const mName = u ? u.name : '알수없음';
                            const mPos = u?.org?.position ? `/ ${u.org.position}` : '';
                            const mDept = u?.org?.dept_name ? `(${u.org.dept_name})` : '';
                            const display = `${mName} ${mPos}${mDept}`;
                            return (
                              <span key={m.user_id} className="badge bg-white text-dark border shadow-sm px-3 py-2 d-flex align-items-center"
                                style={{ fontSize: '0.75rem', fontWeight: '500', minWidth: 0 }} title={display}>
                                <i className="fas fa-user text-secondary me-2 flex-shrink-0"></i>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {display}
                                </span>
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center text-muted py-2">
                          지정된 참조자가 없습니다.
                        </div>
                      )}
                    </div>
                  </li>
              </ul>
              <hr className="text-muted opacity-25 mt-1 mb-2" />

              {/* 🚀 하단 액션 버튼 */}
              <div className="d-flex justify-content-end flex-wrap gap-2 pb-2">
                {/* 수정 권한 */}
                {isLeaderOrAuthor && (
                  isInline ? (
                    <button className="btn btn-outline-primary fw-bold px-2 rounded-3 shadow-sm text-nowrap" onClick={onEditInline}>
                      <i className="fas fa-edit me-1"></i> 수정
                    </button>
                  ) : (
                    <button className="btn btn-outline-primary fw-bold px-2 rounded-3 shadow-sm text-nowrap" onClick={() => navigate(`/pms/projects/edit/${id}`)}>
                      <i className="fas fa-edit me-1"></i> 수정
                    </button>
                  )
                )}

                {!isInline && (
                  <button className="btn btn-secondary fw-bold text-white px-2 rounded-3 shadow-sm text-nowrap" onClick={() => navigate(-1)}>
                    <i className="fas fa-list me-1"></i> 목록
                  </button>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
      {/* 📝 검토 의견 작성 모달 */}
      {showOpinionModal && createPortal(
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99999 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg rounded-4 border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  {modalType === 'approve' ? (
                    <><i className="fas fa-check-circle me-2 text-success"></i>프로젝트 승인</>
                  ) : (
                    <><i className="fas fa-ban me-2 text-danger"></i>프로젝트 반려</>
                  )}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowOpinionModal(false)}></button>
              </div>
              <div className="modal-body">
                <textarea
                  className="form-control"
                  rows="8"
                  placeholder={modalType === 'approve' ? '추가 의견을 입력해 주세요. (선택사항)' : '반려 사유(의견)를 입력해주세요. (필수)'}
                  value={opinionText}
                  onChange={(e) => setOpinionText(e.target.value)}
                  style={{ resize: 'none' }}
                ></textarea>
              </div>
              <div className="modal-footer justify-content-center gap-2">
                {modalType === 'reject' ? (
                  <button type="button" className="btn btn-outline-danger px-4 fw-bold rounded-3 shadow-sm" onClick={() => submitOpinionAction('reject')}>
                    <i className="fas fa-ban me-1"></i>프로젝트 반려
                  </button>
                ) : (
                  <button type="button" className="btn btn-success px-4 fw-bold rounded-3 shadow-sm" onClick={() => submitOpinionAction('approve')}>
                    <i className="fas fa-check-circle me-1"></i>프로젝트 승인
                  </button>
                )}
                <button type="button" className="btn btn-light border px-4 fw-bold text-dark rounded-3 shadow-sm" onClick={() => setShowOpinionModal(false)}>
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 📄 검토 의견 확인 모달 */}
      {showReadOpinionModal && createPortal(
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99999 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg rounded-4 border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold"><i className="fas fa-comment-dots me-2 text-info"></i>검토 의견</h5>
                <button type="button" className="btn-close" onClick={() => setShowReadOpinionModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="p-3 bg-light rounded-3 border" style={{ whiteSpace: 'pre-wrap', minHeight: '100px' }}>
                  {project?.review_opinion}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light border px-4 fw-bold text-dark rounded-3 shadow-sm w-100" onClick={() => setShowReadOpinionModal(false)}>
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default ProjectView;
